/**
 * QuestionFormDialog — Create / Edit dialog for LPA questions.
 *
 * Supports multilingual editing with language tabs when a secondary
 * language is configured, tag assignment, and all question fields.
 */

import { useState, useEffect } from 'react';
import { useLanguage } from '@/core/LanguageContext';
import { PRIMARY_LANGUAGE } from '@/shared/config/languages';
import { useTags, useCreateTag } from '../hooks/useTags';
import type { LpaQuestionWithTags, LpaTag } from '../types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { Badge } from '@/shared/ui/badge';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { Loader2, Plus, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface QuestionFormData {
  question: string;
  questionTranslations: Record<string, string>;
  explanation: string;
  explanationTranslations: Record<string, string>;
  reactionPlan: string;
  reactionPlanTranslations: Record<string, string>;
  estimatedDurationSeconds: number | null;
  isActive: boolean;
  tagIds: string[];
}

function emptyForm(): QuestionFormData {
  return {
    question: '',
    questionTranslations: {},
    explanation: '',
    explanationTranslations: {},
    reactionPlan: '',
    reactionPlanTranslations: {},
    estimatedDurationSeconds: null,
    isActive: true,
    tagIds: [],
  };
}

function formFromQuestion(q: LpaQuestionWithTags): QuestionFormData {
  return {
    question: q.question,
    questionTranslations: q.question_translations ?? {},
    explanation: q.explanation ?? '',
    explanationTranslations: q.explanation_translations ?? {},
    reactionPlan: q.reaction_plan ?? '',
    reactionPlanTranslations: q.reaction_plan_translations ?? {},
    estimatedDurationSeconds: q.estimated_duration_seconds,
    isActive: q.is_active,
    tagIds: q.tags.map((t) => t.id),
  };
}

// ---------------------------------------------------------------------------
// Color presets for inline tag creation (matches TagManager)
// ---------------------------------------------------------------------------

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308',
  '#84cc16', '#22c55e', '#10b981', '#14b8a6',
  '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
];

// ---------------------------------------------------------------------------
// Tag selector (chip-toggle + inline "New Tag" popover)
// ---------------------------------------------------------------------------

function TagSelector({
  allTags,
  selectedIds,
  onChange,
}: {
  allTags: LpaTag[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t, getTranslation, secondaryLanguage, availableLanguages } = useLanguage();
  const createTag = useCreateTag();
  const activeTags = allTags.filter((tg) => tg.is_active);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNameSecondary, setNewNameSecondary] = useState('');
  const [newColor, setNewColor] = useState<string | null>(null);

  const toggle = (id: string) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((i) => i !== id)
        : [...selectedIds, id],
    );
  };

  const resetForm = () => {
    setNewName('');
    setNewNameSecondary('');
    setNewColor(null);
  };

  const handleCreateTag = () => {
    if (!newName.trim()) return;
    const translations: Record<string, string> = {};
    if (secondaryLanguage && newNameSecondary.trim()) {
      translations[secondaryLanguage] = newNameSecondary.trim();
    }
    createTag.mutate(
      {
        name: newName.trim(),
        name_translations: translations,
        color: newColor,
      },
      {
        onSuccess: (created) => {
          // Auto-select the newly created tag
          if (created?.id) {
            onChange([...selectedIds, created.id]);
          }
          resetForm();
          setPopoverOpen(false);
        },
      },
    );
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      {activeTags.map((tag) => {
        const selected = selectedIds.includes(tag.id);
        return (
          <button
            key={tag.id}
            type="button"
            onClick={() => toggle(tag.id)}
            className="transition-all"
          >
            <Badge
              variant={selected ? 'default' : 'outline'}
              className="cursor-pointer text-xs"
              style={
                tag.color
                  ? selected
                    ? { backgroundColor: tag.color, borderColor: tag.color, color: '#fff' }
                    : { borderColor: tag.color, color: tag.color }
                  : undefined
              }
            >
              {getTranslation(tag.name_translations, tag.name)}
            </Badge>
          </button>
        );
      })}

      {/* Inline "New Tag" popover */}
      <Popover open={popoverOpen} onOpenChange={(open) => { setPopoverOpen(open); if (!open) resetForm(); }}>
        <PopoverTrigger asChild>
          <button type="button" className="transition-all">
            <Badge
              variant="outline"
              className="cursor-pointer text-xs gap-1 border-dashed text-muted-foreground hover:text-foreground hover:border-foreground/50"
            >
              <Plus className="h-3 w-3" />
              {t('mod_lpa.questions.newTag', 'New Tag')}
            </Badge>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 space-y-3">
          <div className="space-y-1">
            <p className="text-sm font-medium">
              {t('mod_lpa.questions.createTag', 'Create Tag')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('mod_lpa.questions.createTagHint', 'Quick-create a tag and assign it to this question.')}
            </p>
          </div>

          {/* Name (English) */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {t('mod_lpa.tags.nameEn', 'Name (English)')}
            </label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t('mod_lpa.tags.namePlaceholder', 'e.g. Safety, Quality, 5S...')}
              className="h-8 text-sm"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateTag(); } }}
            />
          </div>

          {/* Secondary language name */}
          {secondaryLanguage && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                {t('mod_lpa.tags.nameSecondary', `Name (${availableLanguages.find(l => l.code === secondaryLanguage)?.name ?? secondaryLanguage})`)}
              </label>
              <Input
                value={newNameSecondary}
                onChange={(e) => setNewNameSecondary(e.target.value)}
                placeholder={t('mod_lpa.tags.nameSecondaryPlaceholder', 'Translation...')}
                className="h-8 text-sm"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateTag(); } }}
              />
            </div>
          )}

          {/* Color picker */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
              {t('mod_lpa.tags.color', 'Color')}
            </label>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                className={`w-6 h-6 rounded border-2 border-dashed transition-all flex items-center justify-center ${
                  newColor === null
                    ? 'ring-2 ring-offset-1 ring-primary scale-110 border-primary'
                    : 'border-muted-foreground/30 hover:scale-105'
                }`}
                onClick={() => setNewColor(null)}
              >
                <X className="h-2.5 w-2.5 text-muted-foreground" />
              </button>
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-6 h-6 rounded transition-all ${
                    newColor === color
                      ? 'ring-2 ring-offset-1 ring-primary scale-110'
                      : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setNewColor(color)}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              className="flex-1"
              disabled={!newName.trim() || createTag.isPending}
              onClick={handleCreateTag}
            >
              {createTag.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
              {t('mod_lpa.tags.save', 'Save')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setPopoverOpen(false)}
              disabled={createTag.isPending}
            >
              {t('mod_lpa.tags.cancel', 'Cancel')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Duration preset buttons
// ---------------------------------------------------------------------------

const DURATION_PRESETS: { label: string; seconds: number | null }[] = [
  { label: '\u2014', seconds: null },
  { label: '30s', seconds: 30 },
  { label: '1 min', seconds: 60 },
  { label: '3 min', seconds: 180 },
  { label: '5 min', seconds: 300 },
  { label: '10 min', seconds: 600 },
  { label: '15 min', seconds: 900 },
];

function DurationPresets({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (seconds: number | null) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {DURATION_PRESETS.map((preset) => {
        const isSelected = value === preset.seconds;
        return (
          <button
            key={preset.label}
            type="button"
            onClick={() => onChange(preset.seconds)}
            className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
              isSelected
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-muted-foreground border-input hover:bg-accent hover:text-accent-foreground'
            }`}
          >
            {preset.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Multilingual field group
// ---------------------------------------------------------------------------

interface MultilingualFieldProps {
  label: string;
  value: string;
  translations: Record<string, string>;
  onChange: (val: string) => void;
  onTranslationChange: (lang: string, val: string) => void;
  multiline?: boolean;
  placeholder?: string;
}

function MultilingualField({
  label,
  value,
  translations,
  onChange,
  onTranslationChange,
  multiline = false,
  placeholder,
}: MultilingualFieldProps) {
  const { availableLanguages, secondaryLanguage } = useLanguage();

  // Single language — just show one field
  if (availableLanguages.length <= 1 || !secondaryLanguage) {
    return (
      <div className="space-y-1.5">
        <Label>{label}</Label>
        {multiline ? (
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={3}
            className="resize-none"
          />
        ) : (
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
          />
        )}
      </div>
    );
  }

  // Multiple languages — tab per language
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Tabs defaultValue={PRIMARY_LANGUAGE}>
        <TabsList className="w-full h-8">
          {availableLanguages.map((lang) => (
            <TabsTrigger key={lang.code} value={lang.code} className="flex-1 text-xs px-2 py-1">
              {lang.flag} {lang.name}
            </TabsTrigger>
          ))}
        </TabsList>
        {availableLanguages.map((lang) => {
          const isPrimary = lang.code === PRIMARY_LANGUAGE;
          const fieldValue = isPrimary ? value : (translations[lang.code] ?? '');
          const handleChange = isPrimary
            ? onChange
            : (val: string) => onTranslationChange(lang.code, val);

          return (
            <TabsContent key={lang.code} value={lang.code} className="mt-2">
              {multiline ? (
                <Textarea
                  value={fieldValue}
                  onChange={(e) => handleChange(e.target.value)}
                  placeholder={placeholder}
                  rows={3}
                  className="resize-none"
                />
              ) : (
                <Input
                  value={fieldValue}
                  onChange={(e) => handleChange(e.target.value)}
                  placeholder={placeholder}
                />
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

interface QuestionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  question?: LpaQuestionWithTags | null;
  onSubmit: (data: {
    question: string;
    question_translations: Record<string, string>;
    explanation: string | null;
    explanation_translations: Record<string, string>;
    reaction_plan: string | null;
    reaction_plan_translations: Record<string, string>;
    estimated_duration_seconds: number | null;
    is_active: boolean;
    tag_ids: string[];
  }) => void;
  isSubmitting: boolean;
}

export default function QuestionFormDialog({
  open,
  onOpenChange,
  question,
  onSubmit,
  isSubmitting,
}: QuestionFormDialogProps) {
  const { t } = useLanguage();
  const { data: tags } = useTags();
  const isEditing = !!question;

  const [form, setForm] = useState<QuestionFormData>(emptyForm);

  // Reset form when dialog opens/closes or question changes
  useEffect(() => {
    if (open) {
      setForm(question ? formFromQuestion(question) : emptyForm());
    }
  }, [open, question]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.question.trim()) return;

    onSubmit({
      question: form.question.trim(),
      question_translations: form.questionTranslations,
      explanation: form.explanation.trim() || null,
      explanation_translations: form.explanationTranslations,
      reaction_plan: form.reactionPlan.trim() || null,
      reaction_plan_translations: form.reactionPlanTranslations,
      estimated_duration_seconds: form.estimatedDurationSeconds,
      is_active: form.isActive,
      tag_ids: form.tagIds,
    });
  };

  const updateField = <K extends keyof QuestionFormData>(
    field: K,
    value: QuestionFormData[K],
  ) => setForm((prev) => ({ ...prev, [field]: value }));

  const updateTranslation = (
    field: 'questionTranslations' | 'explanationTranslations' | 'reactionPlanTranslations',
    lang: string,
    value: string,
  ) =>
    setForm((prev) => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value },
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t('mod_lpa.questions.editTitle', 'Edit Question')
              : t('mod_lpa.questions.createTitle', 'New Question')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Question text (required) */}
          <MultilingualField
            label={t('mod_lpa.questions.questionLabel', 'Question *')}
            value={form.question}
            translations={form.questionTranslations}
            onChange={(val) => updateField('question', val)}
            onTranslationChange={(lang, val) =>
              updateTranslation('questionTranslations', lang, val)
            }
            placeholder={t(
              'mod_lpa.questions.questionPlaceholder',
              'e.g. Is the workstation clean and free of debris?',
            )}
          />

          {/* Explanation / guidance */}
          <MultilingualField
            label={t('mod_lpa.questions.explanationLabel', 'Explanation / Guidance')}
            value={form.explanation}
            translations={form.explanationTranslations}
            onChange={(val) => updateField('explanation', val)}
            onTranslationChange={(lang, val) =>
              updateTranslation('explanationTranslations', lang, val)
            }
            multiline
            placeholder={t(
              'mod_lpa.questions.explanationPlaceholder',
              'Guidance for auditors on what to check...',
            )}
          />

          {/* Reaction plan */}
          <MultilingualField
            label={t('mod_lpa.questions.reactionPlanLabel', 'Reaction Plan')}
            value={form.reactionPlan}
            translations={form.reactionPlanTranslations}
            onChange={(val) => updateField('reactionPlan', val)}
            onTranslationChange={(lang, val) =>
              updateTranslation('reactionPlanTranslations', lang, val)
            }
            multiline
            placeholder={t(
              'mod_lpa.questions.reactionPlanPlaceholder',
              'Steps to take when non-conformance is found...',
            )}
          />

          {/* Tags */}
          <div className="space-y-1.5">
            <Label>{t('mod_lpa.questions.tagsLabel', 'Tags')}</Label>
            <TagSelector
              allTags={tags ?? []}
              selectedIds={form.tagIds}
              onChange={(ids) => updateField('tagIds', ids)}
            />
          </div>

          {/* Duration presets + active row */}
          <div className="flex items-end gap-4">
            <div className="space-y-1.5">
              <Label>
                {t('mod_lpa.questions.durationLabel', 'Est. Duration')}
              </Label>
              <DurationPresets
                value={form.estimatedDurationSeconds}
                onChange={(val) => updateField('estimatedDurationSeconds', val)}
              />
            </div>

            {isEditing && (
              <div className="flex items-center gap-2 pb-1">
                <Switch
                  checked={form.isActive}
                  onCheckedChange={(checked) => updateField('isActive', checked)}
                />
                <Label className="text-sm font-normal">
                  {t('mod_lpa.questions.activeLabel', 'Active')}
                </Label>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('mod_lpa.questions.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!form.question.trim() || isSubmitting}
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditing
                ? t('mod_lpa.questions.saveChanges', 'Save Changes')
                : t('mod_lpa.questions.create', 'Create Question')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
